import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

/**
 * CardRadioGroup
 *
 * Renders a group of card-style radio options with optional icon, title, and description.
 * Card-style radio group with larger click targets and descriptive text.
 *
 * Props:
 * - name: string (html name for inputs)
 * - options: [{ value, label, description?, icon? }]
 * - selected: string (current value)
 * - onChange: func(value)
 * - variant: 'primary' | 'success' | 'purple' (selected state color)
 * - className: string (optional wrapper class)
 */
export default function CardRadioGroup({
  name,
  options,
  selected,
  onChange,
  variant = 'primary',
  className = '',
}) {
  return (
    <div className={classNames('card-radio-group', className)} role="radiogroup" aria-label={name}>
      <div className="d-flex flex-column gap-2">
        {options.map(({ value, label, description, icon }) => {
          const isSelected = selected === value;
          return (
            <div key={value} className="flex-grow-0">
              <input
                type="radio"
                className="btn-check"
                name={name}
                id={`${name}-${value}`}
                autoComplete="off"
                value={value}
                checked={isSelected}
                onChange={() => onChange(value)}
              />
              <label
                htmlFor={`${name}-${value}`}
                className={classNames(
                  'card-radio-group__item',
                  'd-flex align-items-start gap-3 rounded-3 border border-2 p-3 text-start cursor-pointer transition',
                  !isSelected && 'border-gray-200 bg-white',
                  isSelected && 'card-radio-group__item--selected',
                  isSelected && `card-radio-group__item--${variant}`
                )}
              >
                {icon && (
                  <span className="flex-shrink-0 mt-1">
                    <i
                      className={classNames(
                        'bi',
                        icon,
                        'fs-4',
                        !isSelected && 'text-secondary',
                        isSelected && variant === 'primary' && 'text-primary',
                        isSelected && variant === 'success' && 'text-success'
                      )}
                      style={isSelected && variant === 'purple' ? { color: '#7c3aed' } : undefined}
                      aria-hidden
                    />
                  </span>
                )}
                <div className="flex-grow-1 min-w-0">
                  <span className="fw-semibold d-block">{label}</span>
                  {description && (
                    <span className="small text-muted d-block mt-1">{description}</span>
                  )}
                </div>
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
}

CardRadioGroup.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      description: PropTypes.string,
      icon: PropTypes.string,
    })
  ).isRequired,
  selected: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['primary', 'success', 'purple']),
  className: PropTypes.string,
};

CardRadioGroup.defaultProps = {
  selected: '',
  variant: 'primary',
  className: '',
};
